import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { vehicleService } from '../services/vehicleService';
import { formatVehicleTitle } from '../utils/textFormatting';
import PaymentModal from '../components/PaymentModal';
import InventoryVideoModal from '../components/InventoryVideoModal';
import { getCarfaxUrl } from '../data/carfaxReports';

const { FiFilter, FiGrid, FiList, FiHeart, FiPhone } = FiIcons;

const Inventory = () => {
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [filters, setFilters] = useState({
    make: '',
    model: '',
    year: '',
    priceMin: '',
    priceMax: '',
    mileageMax: '',
    fuelType: '',
    transmission: '',
    condition: '',
    status: '',
    vehicleType: 'vehicle',  // Only show cars/SUVs by default, NOT golf carts or trailers
  });
  const [sortBy, setSortBy] = useState('price-low');
  const [viewMode, setViewMode] = useState('grid');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedPaymentType, setSelectedPaymentType] = useState('deposit');

  useEffect(() => {
    window.scrollTo(0, 0);
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    console.log('Loading available vehicles from database...');
    setIsLoading(true);
    setError(null);

    try {
      // Use getAvailableVehicles to exclude sold vehicles from public display
      const { data, error } = await vehicleService.getAvailableVehicles();

      if (error) {
        console.error('Error loading vehicles:', error);
        setError('Failed to load vehicles');
        return;
      }

      console.log('Loaded available vehicles:', data);
      // Log the types of vehicles loaded
      const vehicleTypes = data?.map(v => v.vehicleType || v.vehicle_type || 'vehicle');
      console.log('Vehicle types in inventory:', [...new Set(vehicleTypes)]);
      console.log('Total vehicles:', data?.length, 'Golf carts:', vehicleTypes?.filter(t => t === 'golf-cart').length, 'Trailers:', vehicleTypes?.filter(t => t === 'trailer').length);

      setVehicles(data || []);
      // Don't set filtered vehicles here - let the filter effect handle it

    } catch (err) {
      console.error('Exception loading vehicles:', err);
      setError('Failed to load vehicles');
    } finally {
      setIsLoading(false);
    }
  };

  const openPayment = (vehicle) => {
    setSelectedVehicle(vehicle);
    // Vehicles take a $1,000 deposit; golf carts are full purchase
    const vType = vehicle.vehicleType || vehicle.vehicle_type || 'vehicle';
    setSelectedPaymentType(vType === 'golf-cart' ? 'full' : 'deposit');
    setShowPaymentModal(true);
  };

  const closePayment = () => {
    setShowPaymentModal(false);
    setSelectedVehicle(null);
  };

  useEffect(() => {
    console.log('Applying filters to vehicles:', vehicles.length);
    console.log('Current vehicleType filter:', filters.vehicleType);
    let filtered = vehicles.filter(vehicle => {
      const matchesMake = !filters.make || vehicle.make === filters.make;
      const matchesModel = !filters.model || vehicle.model === filters.model;
      const matchesYear = !filters.year || vehicle.year.toString() === filters.year;
      const matchesPriceMin = !filters.priceMin || vehicle.price >= parseInt(filters.priceMin);
      const matchesPriceMax = !filters.priceMax || vehicle.price <= parseInt(filters.priceMax);
      const matchesMileage = !filters.mileageMax || vehicle.mileage <= parseInt(filters.mileageMax);
      const matchesFuel = !filters.fuelType || vehicle.fuelType === filters.fuelType;
      const matchesTransmission = !filters.transmission || vehicle.transmission === filters.transmission;
      const matchesCondition = !filters.condition || vehicle.condition === filters.condition;
      const matchesStatus = !filters.status || vehicle.status === filters.status;
      // Filter by vehicle type - check both possible field names
      const vType = (vehicle.vehicleType || vehicle.vehicle_type || '').toLowerCase();
      const make = (vehicle.make || '').toLowerCase();
      const model = (vehicle.model || '').toLowerCase();

      // Detect golf carts by make (common golf cart brands)
      const golfCartMakes = ['ezgo', 'e-z-go', 'club car', 'clubcar', 'yamaha', 'icon', 'advanced ev', 'tomberlin', 'star ev', 'bintelli'];
      const isGolfCartByMake = golfCartMakes.some(gcMake => make.includes(gcMake)) ||
        model.includes('golf') || model.includes('cart') ||
        model.includes('txt') || model.includes('rxv') || model.includes('drive2');

      // Detect trailers by make or model
      const trailerIndicators = ['trailer', 'p&c', 'big tex', 'carry-on', 'load trail', 'pj trailer', 'diamond c'];
      const isTrailerByMake = trailerIndicators.some(t => make.includes(t) || model.includes(t));

      // Determine effective vehicle type
      let effectiveType = vType;
      if (!effectiveType || effectiveType === 'vehicle') {
        if (isGolfCartByMake) effectiveType = 'golf-cart';
        else if (isTrailerByMake) effectiveType = 'trailer';
      }

      // Golf carts & trailers removed per owner — ALWAYS exclude them (and boats),
      // regardless of the active filter, so they never surface in inventory.
      let matchesVehicleType = !['golf-cart', 'trailer', 'boat'].includes(effectiveType) &&
        !isGolfCartByMake && !isTrailerByMake;
      if (matchesVehicleType && filters.vehicleType && filters.vehicleType !== 'vehicle') {
        // For any specific automotive type filter, still match exactly
        matchesVehicleType = effectiveType === filters.vehicleType;
      }

      return matchesMake && matchesModel && matchesYear && matchesPriceMin &&
        matchesPriceMax && matchesMileage && matchesFuel && matchesTransmission &&
        matchesCondition && matchesStatus && matchesVehicleType;
    });

    console.log('Filtered vehicles count:', filtered.length);
    const filteredTypes = filtered.map(v => v.vehicleType || v.vehicle_type || 'vehicle');
    console.log('Filtered vehicle types:', [...new Set(filteredTypes)]);

    // Sort vehicles
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price-low': return a.price - b.price;
        case 'price-high': return b.price - a.price;
        case 'year-new': return b.year - a.year;
        case 'year-old': return a.year - b.year;
        case 'mileage-low': return a.mileage - b.mileage;
        case 'mileage-high': return b.mileage - a.mileage;
        default: return 0;
      }
    });

    console.log('Filtered vehicles:', filtered.length);
    setFilteredVehicles(filtered);
  }, [vehicles, filters, sortBy]);

  const handleFilterChange = (key, value) => {
    console.log('Filter changed:', key, value);
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      make: '',
      model: '',
      year: '',
      priceMin: '',
      priceMax: '',
      mileageMax: '',
      fuelType: '',
      transmission: '',
      condition: '',
      status: '',
      vehicleType: '',  // Empty string to show all vehicles
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatMileage = (mileage) => {
    return new Intl.NumberFormat('en-US').format(mileage);
  };

  // Create unique lists for filters
  const uniqueMakes = vehicles.length > 0 ? [...new Set(vehicles.map(v => v.make))].sort() : [];
  const uniqueYears = vehicles.length > 0 ? [...new Set(vehicles.map(v => v.year))].sort((a, b) => b - a) : [];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading vehicles...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 pt-24">
        <div className="container mx-auto px-4">
          <div className="text-center py-16 bg-white rounded-2xl shadow-lg max-w-2xl mx-auto">
            <div className="text-6xl mb-6">🔧</div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Inventory Temporarily Unavailable</h1>
            <p className="text-gray-600 mb-2">We're experiencing a brief technical issue with our inventory system.</p>
            <p className="text-gray-600 mb-8">Our vehicles are still available — give us a call and we'll help you find exactly what you're looking for!</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+18173822316"
                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors text-lg flex items-center justify-center gap-2"
              >
                <FiPhone className="w-5 h-5" />
                Call Us: (817) 382-2316
              </a>
              <button
                onClick={loadVehicles}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold transition-colors text-lg"
              >
                Try Again
              </button>
            </div>
            <p className="text-sm text-gray-400 mt-8">Paradise-Performance — 305 Co Rd 3470, Paradise, TX 76073</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-gray-50 py-8 pb-20 pt-24 lg:pt-36 md:pt-16 sm:pt-20 overflow-x-hidden"
    >
      <InventoryVideoModal />
      <Helmet>
        <title>Complete Inventory | Pre-Owned Cars & SUVs | Paradise-Performance</title>
        <meta name="description" content="Browse our complete inventory of quality pre-owned cars and SUVs in Paradise, TX. Filter by make, price, year. Financing available." />
        <link rel="canonical" href="https://paradise-performance.com/inventory" />
        <meta property="og:title" content="Complete Vehicle Inventory - Paradise-Performance" />
        <meta property="og:description" content="Browse all pre-owned cars & SUVs in stock. Financing available." />
        <meta property="og:url" content="https://paradise-performance.com/inventory" />
      </Helmet>

      <div className="container mx-auto px-4 overflow-x-hidden max-w-full">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Vehicle Inventory</h1>
          <p className="text-gray-600 mb-2">
            Browse our selection of {filteredVehicles.length} quality used vehicles in Paradise, TX serving Dallas, Fort Worth, and Denton
          </p>
          <p className="text-sm text-primary-600 font-medium">
            <span className="inline-flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              We offer in-house financing on select vehicles
            </span>
          </p>
          <p className="text-sm text-gray-500 mt-2">
            If you've placed a deposit or purchased a vehicle, pickup is available at our lot — 305 Co Rd 3470, Paradise, TX 76073. Call <a href="tel:8173822316" className="text-primary-600 font-medium hover:underline">(817) 382-2316</a> to schedule a test drive or arrange pickup.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 overflow-x-hidden">
          {/* Filters Sidebar */}
          <div className="w-full lg:w-1/4">
            <div className="bg-white rounded-lg shadow-lg p-4 lg:p-6 lg:sticky lg:top-24">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
                <button
                  onClick={clearFilters}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  Clear All
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Make</label>
                  <select
                    value={filters.make}
                    onChange={(e) => handleFilterChange('make', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Makes</option>
                    {uniqueMakes.map(make => (
                      <option key={make} value={make}>{make}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Status</option>
                    <option value="in-transit">In Transit</option>
                    <option value="recon-pending">Recon Pending</option>
                    <option value="on-lot">Available Now</option>
                    <option value="pending-contract">Sale Pending</option>
                    <option value="sold">Sold</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                  <select
                    value={filters.year}
                    onChange={(e) => handleFilterChange('year', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Years</option>
                    {uniqueYears.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Price Range</label>
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      placeholder="Min"
                      value={filters.priceMin}
                      onChange={(e) => handleFilterChange('priceMin', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      value={filters.priceMax}
                      onChange={(e) => handleFilterChange('priceMax', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fuel Type</label>
                  <select
                    value={filters.fuelType}
                    onChange={(e) => handleFilterChange('fuelType', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Fuel Types</option>
                    <option value="Gasoline">Gasoline</option>
                    <option value="Electric">Electric</option>
                    <option value="Hybrid">Hybrid</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="w-full lg:w-3/4 overflow-x-hidden">
            {/* Toolbar */}
            <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="text-sm text-gray-600">
                  Showing {filteredVehicles.length} of {vehicles.length} vehicles
                </div>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700">Sort by:</label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="price-low">Price: Low to High</option>
                      <option value="price-high">Price: High to Low</option>
                      <option value="year-new">Year: Newest First</option>
                      <option value="year-old">Year: Oldest First</option>
                      <option value="mileage-low">Mileage: Low to High</option>
                      <option value="mileage-high">Mileage: High to Low</option>
                    </select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded transition-colors ${viewMode === 'grid' ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                    >
                      <SafeIcon icon={FiGrid} className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded transition-colors ${viewMode === 'list' ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                    >
                      <SafeIcon icon={FiList} className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Vehicle Grid with Metallic Style */}
            <style>{`
              .metallic-card {
                position: relative;
                background: linear-gradient(135deg, #e0e0e0, #c0c0c0);
                border-radius: 14px;
                box-shadow:
                  0 2px 8px rgba(0, 0, 0, 0.15),
                  0 1px 3px rgba(0, 0, 0, 0.1);
                transition:
                  transform 0.3s ease,
                  box-shadow 0.3s ease;
                overflow: hidden;
                display: flex;
                flex-direction: column;
                max-width: 100%;
              }
              
              @media (min-width: 768px) {
                .metallic-card {
                  min-height: 410px;
                  border-radius: 20px;
                  box-shadow:
                    0 4px 16px rgba(0, 0, 0, 0.12),
                    0 2px 6px rgba(0, 0, 0, 0.08);
                }
              }

              .metallic-card.golf-cart-card {
                background: linear-gradient(135deg, #3a3a3a, #1a1a1a);
                box-shadow:
                  6px 6px 12px rgba(0, 0, 0, 0.5),
                  -6px -6px 12px rgba(100, 100, 100, 0.1),
                  inset 2px 2px 4px rgba(80, 80, 80, 0.3),
                  inset -2px -2px 4px rgba(0, 0, 0, 0.8);
              }

              .metallic-card::before {
                content: "";
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: linear-gradient(
                  45deg,
                  transparent,
                  rgba(255, 255, 255, 0.15),
                  transparent
                );
                background-size: 200% 200%;
                animation: shine 4s infinite ease-in-out;
                border-radius: inherit;
                pointer-events: none;
                z-index: 1;
              }

              .golf-cart-card::before {
                background: linear-gradient(
                  45deg,
                  transparent,
                  rgba(150, 150, 150, 0.5),
                  transparent
                );
              }

              @media (min-width: 768px) {
                .metallic-card:hover {
                  transform: translateY(-4px);
                  box-shadow:
                    0 12px 32px rgba(0, 0, 0, 0.18),
                    0 4px 12px rgba(0, 0, 0, 0.1);
                }

                .golf-cart-card:hover {
                  box-shadow:
                    0 12px 32px rgba(0, 0, 0, 0.35),
                    0 4px 12px rgba(0, 0, 0, 0.2);
                }
              }

              .metallic-card-content {
                position: relative;
                z-index: 2;
                flex: 1;
                display: flex;
                flex-direction: column;
              }

              @keyframes shine {
                0% {
                  background-position: 0% 50%;
                }
                50% {
                  background-position: 100% 50%;
                }
                100% {
                  background-position: 0% 50%;
                }
              }

              .metallic-image-container {
                position: relative;
                overflow: hidden;
                background: #e8e8e8;
                aspect-ratio: 4 / 3;
              }

              @media (min-width: 768px) {
                .metallic-image-container {
                  aspect-ratio: 16 / 10;
                }
              }

              .metallic-image-container img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                object-position: center;
                transition: opacity 0.4s ease, transform 0.5s ease;
              }

              .metallic-image-container img.img-loading {
                opacity: 0;
              }

              .metallic-image-container img.img-loaded {
                opacity: 1;
              }

              .metallic-card:hover .metallic-image-container img {
                transform: scale(1.05);
              }

              .metallic-image-container .img-skeleton {
                position: absolute;
                inset: 0;
                background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
                background-size: 200% 100%;
                animation: skeleton-pulse 1.5s ease-in-out infinite;
              }

              .golf-cart-card .metallic-image-container .img-skeleton {
                background: linear-gradient(90deg, #303030 25%, #404040 50%, #303030 75%);
                background-size: 200% 100%;
              }

              @keyframes skeleton-pulse {
                0% { background-position: 200% 0; }
                100% { background-position: -200% 0; }
              }

              .img-overlay {
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                height: 30%;
                background: linear-gradient(to top, rgba(0,0,0,0.35), transparent);
                pointer-events: none;
                z-index: 3;
              }

              .photo-count-badge {
                position: absolute;
                bottom: 10px;
                right: 10px;
                background: rgba(0,0,0,0.65);
                backdrop-filter: blur(4px);
                color: white;
                font-size: 12px;
                font-weight: 600;
                padding: 4px 10px;
                border-radius: 6px;
                display: flex;
                align-items: center;
                gap: 5px;
                z-index: 4;
              }

              .photo-count-badge svg {
                width: 14px;
                height: 14px;
              }

              .metallic-button {
                background: linear-gradient(135deg, #2563eb, #1d4ed8);
                border: none;
                margin-top: auto;
                border-radius: 10px;
                transition: all 0.2s ease;
                font-size: 14px;
                display: flex;
                align-items: center;
                justify-content: center;
              }

              .metallic-button:hover {
                background: linear-gradient(135deg, #3b82f6, #2563eb);
                transform: translateY(-1px);
                box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
              }

              .metallic-heart {
                background: rgba(255,255,255,0.9);
                backdrop-filter: blur(4px);
                border-radius: 50%;
                box-shadow: 0 2px 8px rgba(0,0,0,0.15);
              }

              .metallic-heart:hover {
                background: #ff4444;
                color: white;
              }
              
              .metallic-heart:hover .text-gray-600 {
                color: white !important;
              }
              
              /* Golf Cart Card Specific Text Styles */
              .golf-cart-card .text-gray-800 {
                color: #e0e0e0 !important;
              }
              
              .golf-cart-card .text-gray-700 {
                color: #d0d0d0 !important;
              }
              
              .golf-cart-card .text-gray-600 {
                color: #b0b0b0 !important;
              }
              
              .golf-cart-card .text-gray-500 {
                color: #a0a0a0 !important;
              }
              
              .golf-cart-card .bg-gradient-to-br.from-gray-200.to-gray-300 {
                background: linear-gradient(135deg, #505050, #404040) !important;
                color: #e0e0e0 !important;
              }
              
              .golf-cart-card .metallic-button {
                background: linear-gradient(135deg, #2a7a2a, #1a5a1a) !important;
                box-shadow:
                  2px 2px 4px rgba(0, 0, 0, 0.4),
                  -2px -2px 4px rgba(100, 100, 100, 0.2),
                  inset 1px 1px 2px rgba(150, 255, 150, 0.2) !important;
              }
              
              .golf-cart-card .metallic-button:hover {
                background: linear-gradient(135deg, #3a8a3a, #2a6a2a) !important;
                box-shadow:
                  3px 3px 6px rgba(0, 0, 0, 0.45),
                  -3px -3px 6px rgba(100, 100, 100, 0.25),
                  inset 1px 1px 2px rgba(150, 255, 150, 0.3) !important;
              }
              
              .golf-cart-card .metallic-heart {
                background: linear-gradient(135deg, #505050, #303030) !important;
                box-shadow:
                  2px 2px 4px rgba(0, 0, 0, 0.3),
                  -2px -2px 4px rgba(100, 100, 100, 0.2) !important;
              }
              
              .golf-cart-card .metallic-image-container {
                background: linear-gradient(135deg, #404040, #202020) !important;
                box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.4) !important;
              }
              
              .golf-cart-card .bg-gradient-to-br.from-blue-600.to-blue-800 {
                background: linear-gradient(135deg, #4ade80, #22c55e) !important;
                -webkit-background-clip: text !important;
                -webkit-text-fill-color: transparent !important;
                background-clip: text !important;
              }
              
              .golf-cart-card .bg-gradient-to-br.from-blue-200.to-blue-300 {
                background: linear-gradient(135deg, #606060, #505050) !important;
                color: #10b981 !important;
              }
            `}</style>
            {filteredVehicles.length > 0 ? (
              <div className={`grid gap-4 mb-12 pb-8 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'}`}>
                {filteredVehicles.map((vehicle) => (
                  <motion.div
                    key={vehicle.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`metallic-card ${(vehicle.vehicleType === 'golf-cart' ||
                      vehicle.vehicle_type === 'golf-cart' ||
                      (vehicle.vehicleType || vehicle.vehicle_type || '').toLowerCase().includes('golf'))
                      ? 'golf-cart-card' : ''
                      }`}
                  >
                    <div className="metallic-card-content flex flex-col h-full">
                      <div className="metallic-image-container">
                        {/* Skeleton loader */}
                        <div className="img-skeleton" id={`skeleton-${vehicle.id}`} />
                        <img
                          src={vehicle.images?.[0] || '/assets/assets/images/business-shop-sign.webp'}
                          alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                          className="img-loading"
                          loading="lazy"
                          onLoad={(e) => {
                            e.target.classList.remove('img-loading');
                            e.target.classList.add('img-loaded');
                            const skeleton = document.getElementById(`skeleton-${vehicle.id}`);
                            if (skeleton) skeleton.style.display = 'none';
                          }}
                          onError={(e) => {
                            e.target.src = '/assets/assets/images/business-shop-sign.webp';
                            e.target.classList.remove('img-loading');
                            e.target.classList.add('img-loaded');
                            const skeleton = document.getElementById(`skeleton-${vehicle.id}`);
                            if (skeleton) skeleton.style.display = 'none';
                          }}
                        />
                        {/* Bottom gradient overlay for depth */}
                        <div className="img-overlay" />
                        {/* CARFAX vehicle-history badge */}
                        {(vehicle.carfaxUrl || getCarfaxUrl(vehicle.vin)) && (
                          <a
                            href={vehicle.carfaxUrl || getCarfaxUrl(vehicle.vin)}
                            target="_blank"
                            rel="nofollow noindex noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="carfax-badge absolute top-2 right-2 z-20"
                            title="View this vehicle's history report"
                          >
                            <img src="/assets/images/carfax-badge.svg" alt="View CARFAX vehicle history report" loading="lazy" />
                          </a>
                        )}
                        {/* Photo count badge */}
                        {vehicle.images && vehicle.images.length > 1 && (
                          <div className="photo-count-badge">
                            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h2l2-3h10l2 3h2a1 1 0 011 1v11a1 1 0 01-1 1H3a1 1 0 01-1-1V8a1 1 0 011-1z" />
                              <circle cx="12" cy="13" r="4" />
                            </svg>
                            {vehicle.images.length}
                          </div>
                        )}
                        {/* Status & condition badges */}
                        <div className="absolute top-3 left-3 flex flex-col gap-2 z-10">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-lg ${vehicle.condition === 'new' ? 'bg-gradient-to-r from-green-400 to-green-600 text-white' :
                            vehicle.condition === 'professional' ? 'bg-gradient-to-r from-purple-400 to-purple-600 text-white' :
                              'bg-gradient-to-r from-blue-400 to-blue-600 text-white'
                            }`}>
                            {vehicle.condition === 'professional' ? 'Professional' :
                              vehicle.condition === 'new' ? 'New' : 'Used'}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-lg ${vehicle.status === 'in-transit' ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white' :
                            vehicle.status === 'recon-pending' ? 'bg-gradient-to-r from-orange-400 to-orange-600 text-white' :
                              vehicle.status === 'on-lot' ? 'bg-gradient-to-r from-green-400 to-green-600 text-white' :
                                vehicle.status === 'pending-contract' ? 'bg-gradient-to-r from-gray-700 to-black text-white' :
                                  'bg-gradient-to-r from-cyan-400 to-cyan-600 text-white'
                            }`}>
                            {vehicle.status === 'in-transit' ? 'In Transit' :
                              vehicle.status === 'recon-pending' ? 'Recon Pending' :
                                vehicle.status === 'on-lot' ? 'In Stock' :
                                  vehicle.status === 'pending-contract' ? 'Sale Pending' :
                                    'Sold'}
                          </span>
                          {((vehicle.vehicleType || vehicle.vehicle_type || '').toLowerCase().includes('golf')) && (
                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-emerald-400 to-emerald-600 text-white shadow-lg">
                              Golf Cart
                            </span>
                          )}
                        </div>
                        <div className="absolute top-3 right-3 z-10">
                          <button className="metallic-heart p-2 transition-all">
                            <SafeIcon icon={FiHeart} className="w-4 h-4 text-gray-600" />
                          </button>
                        </div>
                      </div>

                      <div className="p-4 md:p-5 flex flex-col flex-1">
                        {/* Content Section */}
                        <div>
                          <div className="mb-2">
                            <h3 className="text-base md:text-lg font-bold text-gray-800 leading-tight mb-0.5">
                              {formatVehicleTitle(vehicle)}
                            </h3>
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-gray-500">Stock #{vehicle.stockNumber}</p>
                              <div className="text-lg md:text-xl font-bold text-blue-700">
                                {formatPrice(vehicle.price)}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center text-sm text-gray-600 mb-2 space-x-4">
                            <span>{formatMileage(vehicle.mileage)} mi</span>
                            <span>•</span>
                            <span>{vehicle.fuelType}</span>
                            <span>•</span>
                            <span>{vehicle.combinedMPG} MPG</span>
                          </div>

                          {vehicle.vin && (
                            <div className="text-sm text-gray-500 font-mono truncate mb-2" title={vehicle.vin}>
                              VIN: {vehicle.vin}
                            </div>
                          )}

                          {vehicle.features && vehicle.features.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3 md:mb-4">
                              {vehicle.features?.slice(0, 3).map((feature, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-gradient-to-br from-gray-200 to-gray-300 text-gray-700 text-xs rounded-md font-medium shadow-sm"
                                >
                                  {feature}
                                </span>
                              ))}
                              {vehicle.features?.length > 3 && (
                                <span className="px-2 py-1 bg-gradient-to-br from-blue-200 to-blue-300 text-blue-800 text-xs rounded-md font-bold shadow-sm">
                                  +{vehicle.features.length - 3} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Buttons Section */}
                        <div className="flex gap-2 mt-3 pt-3" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                          <Link
                            to={(() => {
                              const vehicleType = (vehicle.vehicleType || vehicle.vehicle_type || '').toLowerCase();

                              if (vehicleType.includes('golf') || vehicleType.includes('cart')) {
                                return `/golf-cart/${vehicle.id}`;
                              } else if (vehicleType.includes('trailer')) {
                                return `/trailer/${vehicle.id}`;
                              } else {
                                // Default route for regular vehicles
                                return `/vehicle/${vehicle.id}`;
                              }
                            })()}
                            className="flex-1 metallic-button text-white px-4 py-3 font-semibold text-center text-sm"
                          >
                            View Details
                          </Link>
                          <a
                            href="tel:+18173822316"
                            className="metallic-button text-white px-4 py-3 font-semibold"
                            title="Call about this vehicle"
                          >
                            <SafeIcon icon={FiPhone} className="w-4 h-4" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 metallic-card p-8">
                <div className="metallic-card-content">
                  <div className="text-gray-700 text-lg mb-4 font-semibold">
                    {vehicles.length === 0 ? 'No vehicles available' : 'No vehicles match your search criteria'}
                  </div>
                  <p className="text-gray-600 mb-6">
                    Try adjusting your filters or contact us for assistance finding your perfect vehicle.
                  </p>
                  <button
                    onClick={clearFilters}
                    className="metallic-button text-white px-6 py-3 font-bold"
                  >
                    Clear All Filters
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={closePayment}
        vehicle={selectedVehicle}
        paymentType={selectedPaymentType}
        maxAmount={100000}
      />
      {/* Policy Notice */}
      <div className="container mx-auto px-4 py-4 pb-16 text-center">
        <p className="text-xs text-gray-500">
          All deposits are non-refundable. Please review our{' '}
          <Link to="/refund-policy" className="text-primary-600 hover:text-primary-700 underline">
            Refund & Return Policy
          </Link>{' '}
          before making a purchase.
        </p>
      </div>
    </motion.div>
  );
};

export default Inventory;